import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

import CheckboxField from '../inputs/CheckboxField'
import { Pinline } from '../Pinline'
import { MultiSelectFilter } from './MultiSelectFilter'
import { SelectFilter } from './SelectFilter'
import { type FilterConfig, getAllFilterNames, isMultiName } from './types'

const getRawValues = (targetName: string, searchParams: URLSearchParams) => {
  if (isMultiName(targetName)) return searchParams.getAll(targetName)
  const value = searchParams.get(targetName)
  return value ? [value] : []
}

type FilterControlProps = FilterConfig & {
  queryString: string
  setQueryString: Dispatch<SetStateAction<string>>
  openDatePicker?: string | null
  setOpenDatePicker?: Dispatch<SetStateAction<string | null>>
  setFilterEnabled?: (name: string, enabled: boolean) => void
}

const FilterControl = ({
  FilterComponent,
  label,
  name,
  options,
  queryString,
  setQueryString,
  openDatePicker,
  setOpenDatePicker,
  relatedNames,
  setFilterEnabled,
}: FilterControlProps) => {
  const checkboxName = `${name}_checkbox`
  const resolvedOptions = options ?? []
  const firstOption = resolvedOptions[0]
  const isMulti = isMultiName(name)

  const params = useMemo(() => new URLSearchParams(queryString), [queryString])

  const allNames = useMemo(
    () => getAllFilterNames({ name, relatedNames } as FilterConfig),
    [name, relatedNames],
  )

  const hasAnyValue = allNames.some(
    currentName => getRawValues(currentName, params).length > 0,
  )
  const [isMultiEnabled, setIsMultiEnabled] = useState(hasAnyValue)

  const value = isMulti ? params.getAll(name) : (params.get(name) ?? '')
  const isSelected = isMulti ? isMultiEnabled || hasAnyValue : hasAnyValue

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked

      if (isMulti) {
        setIsMultiEnabled(checked)
      }

      setFilterEnabled?.(name, checked)

      setQueryString(qs => {
        const params = new URLSearchParams(qs)

        if (checked) {
          if (!isMulti) {
            params.append(name, firstOption?.value)
          }
        } else {
          allNames.forEach(n => params.delete(n))
        }

        return params.toString()
      })
      setOpenDatePicker?.(null)
    },
    [
      firstOption,
      name,
      setQueryString,
      setOpenDatePicker,
      isMulti,
      allNames,
      setFilterEnabled,
    ],
  )

  return (
    <div>
      <div className="flex items-center">
        <span>{label}</span>
        <Pinline dotted flex className="mx-2 !border-fv-blue-300" />
        <CheckboxField
          checked={isSelected}
          alternate
          name={checkboxName}
          onChange={onChange}
        />
      </div>

      {isSelected &&
        (FilterComponent ? (
          <FilterComponent
            name={name}
            options={resolvedOptions}
            setQueryString={setQueryString}
            value={value}
            queryString={queryString}
            openDatePicker={openDatePicker}
            setOpenDatePicker={setOpenDatePicker}
          />
        ) : isMulti ? (
          <MultiSelectFilter
            name={name}
            options={resolvedOptions}
            setQueryString={setQueryString}
            value={value as string[]}
          />
        ) : (
          <SelectFilter
            name={name}
            options={resolvedOptions}
            setQueryString={setQueryString}
            value={value as string}
          />
        ))}
    </div>
  )
}

export default FilterControl
